/* ── The room's side channel ───────────────────────────────────────────
   A guided tour needs a way to carry small live signals between the host
   and the buyers — the raised hand today, more later. This is that
   channel, and the shape of it is dictated by what the sync server will
   actually carry rather than by what we'd design from scratch.

   ── WHY WE RIDE A "sync" FRAME ──
   `wss://vrsync…` is the relay the VR engine already uses to keep
   everyone's camera on the same spot. Probed on 2026-08-20 with two
   clients in one room, it behaves like this:

     join with no token          → accepted
     {type:"sync", …}            → relayed to the room, byte-identical
     {type:"hand", …}            → SILENTLY DROPPED
     unknown fields on a "sync"  → passed through untouched

   So the server allow-lists message *types* but not *fields*. A hand sent
   as its own type never arrives; a hand sent as extra fields on a `sync`
   frame does. Hence the `gr*` fields below — namespaced so the engine,
   which reads `nodeId` and `pose` off the same frames and ignores
   everything else, can't mistake one of ours for a camera move.

   This is a workaround and it is meant to be replaced: when the sync
   server learns a real `hand` type, only `handFrame` and `parseHand`
   change. Nothing above them knows the frame shape.

   ── WHAT MUST NOT GO OVER THIS ──
   The relay is UNAUTHENTICATED. Anyone holding a room id can join it,
   read it and write to it. A raised hand is a boolean — worth nothing to
   an eavesdropper and forgeable only into a slightly confusing UI. Chat
   is not: it carries what a buyer says about budget and family. Chat
   waits for `ws_token` and a server that verifies it. Do not add it here.

   ── IDENTITY ──
   Frames are keyed by the participant's `session_id`, not the Agora uid.
   The host's people list is already keyed that way, so a hand maps
   straight onto a row with nothing to look up. */

export const RELAY_VERSION = 1;

/* Where the relay lives, or "" for nowhere.

   ── WHY THERE IS NO GLOBAL DEFAULT ──
   Only ONE of these servers exists: vrsync.dev.opensky.co.in. Checked
   2026-08-20 — vrsync.opensky.co.in and vrsync.stage.opensky.co.in are
   both NXDOMAIN. So a hard-coded fallback would mean a buyer on
   openskyvr.com quietly announcing their raised hand on the development
   cluster, in a room shared with whatever anyone happens to be testing.
   Cross-environment traffic is not a thing to arrange by accident.

   So: the variable decides, and only localhost gets a convenience
   default. Anywhere else, an unset variable means the channel is off and
   the control says so — which is the same rule the rest of this room
   follows for anything not wired up. When a stage/prod relay is
   deployed, set REACT_APP_VRSYNC_URL for that environment in Netlify and
   the hand starts working there with no code change.

   ── HOSTNAME AS A PARAMETER ──
   Passed in with a sensible default rather than read from `window`
   inside, which keeps this a pure function — the difference between
   testing the rule and fighting jsdom over a read-only location. */
const currentHost = (): string =>
  (typeof window !== "undefined" && window.location?.hostname) || "";

const isLocalHost = (hostname: string): boolean =>
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(hostname);

export const relayUrl = (hostname: string = currentHost()): string => {
  const configured = String(process.env.REACT_APP_VRSYNC_URL || "").trim();
  if (configured) return configured;
  return isLocalHost(hostname) ? "wss://vrsync.dev.opensky.co.in/" : "";
};

/* Is there a channel at all? Pages ask this to decide whether the hand is
   a working control or a disabled one with a reason attached. */
export const relayAvailable = (hostname: string = currentHost()): boolean =>
  !!relayUrl(hostname);

/* ── Reading the ws_token response ─────────────────────────────────────
   The 200 body is untyped in the OpenAPI spec and the backend hadn't
   deployed the endpoint when this was written, so the field names are
   genuinely unknown — asked, not guessed. Rather than pick one spelling
   and ship a client that breaks on contact, read the plausible ones and
   let whichever arrives win.

   Delete the alternatives once the real contract is confirmed. A reader
   this permissive is a placeholder for an answer, not a design. */
export type WsGrant = { token: string; url: string; roomId: string };

const firstString = (obj: any, keys: string[]): string => {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

export const readWsGrant = (body: any, fallbackRoom: string): WsGrant | null => {
  if (!body || typeof body !== "object") return null;
  const token = firstString(body, ["token", "ws_token", "jwt", "access_token"]);
  if (!token) return null;                       // no token, no grant
  return {
    token,
    /* A URL from the backend WINS over our env var: it knows which relay
       belongs to the environment that minted the token, and a token
       signed for one relay is worthless at another. The env var is the
       fallback for the period before the endpoint exists. */
    url: firstString(body, ["url", "ws_url", "socket_url", "wss_url", "endpoint"]),
    roomId: firstString(body, ["room_id", "room", "channel"]) || String(fallbackRoom || ""),
  };
};

/* A raised hand is state, not an event, so it has to survive a host who
   joins after the buyer raised it. The relay has no retained state and
   the server won't answer "who has a hand up?", so the holder re-states
   it on a timer. Eight seconds is under the ten a host takes to notice
   anything, and one small frame per raised hand is nothing next to the
   camera stream already on this channel. */
export const HAND_REANNOUNCE_MS = 8000;

export type HandSignal = {
  sessionId: string;
  raised: boolean;
  /** Only for a host whose roster hasn't caught up with a new arrival. */
  name: string;
};

export type RelayRole = "guide" | "viewer";

export const joinFrame = (room: string, sessionId: string, role: RelayRole): string =>
  JSON.stringify({ type: "join", room, role, uid: sessionId });

/* ── TWO RELAYS, ONE CLIENT ────────────────────────────────────────────
   There are two servers in the world right now and this code has to work
   against both, because which one answers depends on an env var and on
   whose deploy landed most recently:

     LEGACY   vrsync.dev — 50 lines, no auth, relays only
              sync|offer|answer|ice. What the VR engine uses today.
     AUTHED   vrsync-auth.dev — RS256 join tokens, per-session presence,
              and `sync` restricted to participants holding "drive".

   The difference matters for one specific reason: on the authenticated
   server a BUYER may not send `sync`, and a raised hand is exactly the
   thing a non-driver needs to send. So the hand cannot ride a sync frame
   there. It rides its own `hand` type instead — which the legacy server
   drops, and which the authenticated one relays once `hand` is added to
   its RELAY_TYPES (a two-line change, asked for separately).

   We do not guess which server we're on. The authenticated one answers a
   successful join with {type:"joined", …}; nothing else does. That frame
   is the capability signal — see parseJoined. Until it arrives we speak
   legacy, and we switch the moment it does. */
export const authJoinFrame = (
  room: string,
  sessionId: string,
  role: RelayRole,
  token: string,
): string =>
  /* Field order and names are the frozen Day-0 contract: the FIRST frame
     must be exactly this or the socket closes 4401. `role` is carried for
     the legacy server's benefit — the authenticated one ignores it and
     takes authority from the token's privileges, which is the correct
     thing to do with anything a client asserts about itself. */
  JSON.stringify({ type: "join", token, room, uid: sessionId, role });

/* The hand as its own message type, for the authenticated relay. Carries
   the same gr* fields as the sync-borne version so one parser reads both
   and neither side has to know which shape it received. */
export const typedHandFrame = (
  room: string,
  sessionId: string,
  raised: boolean,
  name = "",
): string =>
  JSON.stringify({
    type: "hand",
    room,
    from: "guided-room",
    uid: sessionId,
    grV: RELAY_VERSION,
    grKind: "hand",
    grHand: !!raised,
    grName: name,
  });

/* Did the authenticated server accept our join? Returns the privileges it
   granted, or null for any other frame.

   `privileges` is worth reading rather than assuming: it is the server's
   answer to "may this participant drive?", and a host whose token came
   back without "drive" has a backend problem worth surfacing early
   rather than discovering when the walkthrough won't move. */
export const parseJoined = (
  data: unknown,
  room: string,
): { sessionId: string; privileges: string[] } | null => {
  let msg: any;
  try {
    msg = typeof data === "string" ? JSON.parse(data) : data;
  } catch {
    return null;
  }
  if (!msg || typeof msg !== "object") return null;
  if (msg.type !== "joined") return null;
  if (String(msg.room || "") !== String(room)) return null;
  return {
    sessionId: String(msg.session_id || ""),
    privileges: Array.isArray(msg.privileges) ? msg.privileges.map(String) : [],
  };
};

export const handFrame = (
  room: string,
  sessionId: string,
  raised: boolean,
  name = "",
): string =>
  JSON.stringify({
    type: "sync",          // the only type the relay carries — see above
    room,
    from: "guided-room",
    uid: sessionId,
    grV: RELAY_VERSION,
    grKind: "hand",
    grHand: !!raised,
    grName: name,
  });

/* Read a hand off the wire, or null for anything that isn't one.

   Deliberately strict. This socket carries the engine's camera sync as
   well as our frames, and in a room where a walkthrough is running most
   messages are NOT for us. Every guard below drops one of those classes
   rather than letting a pose update reach the host's people list.

   `selfId` drops our own frames. The server doesn't echo to the sender
   today, but "today" is doing a lot of work in that sentence — a relay
   that starts echoing shouldn't make a buyer's own hand appear as an
   incoming request. */
export const parseHand = (
  data: unknown,
  room: string,
  selfId = "",
): HandSignal | null => {
  let msg: any;
  try {
    msg = typeof data === "string" ? JSON.parse(data) : data;
  } catch {
    return null;                                   // not JSON at all
  }
  if (!msg || typeof msg !== "object") return null;
  /* Both carriers: `sync` on the legacy relay (fields smuggled through a
     type it will actually forward) and `hand` on the authenticated one,
     where a buyer may not send `sync` at all. */
  if (msg.type !== "sync" && msg.type !== "hand") return null;
  if (String(msg.room || "") !== String(room)) return null;  // another room
  if (Number(msg.grV) !== RELAY_VERSION) return null;        // engine pose, or a newer us
  if (msg.grKind !== "hand") return null;          // ours, but not a hand
  const sessionId = String(msg.uid || "").trim();
  if (!sessionId) return null;                     // unattributable
  if (selfId && sessionId === selfId) return null; // our own, echoed back
  return {
    sessionId,
    raised: !!msg.grHand,
    name: String(msg.grName || "").trim(),
  };
};

/* The host answering someone puts THEIR hand down. Without this the
   buyer has to remember to lower it, and they don't — they got their
   answer and moved on — so the host's queue fills with questions that
   were dealt with ten minutes ago and stops being worth reading.

   Addressed by `grTarget` rather than sent as a broadcast because the
   relay has no concept of a private message: everyone in the room sees
   this frame and everyone but the target ignores it. */
export const ackFrame = (
  room: string,
  hostSessionId: string,
  targetSessionId: string,
): string =>
  JSON.stringify({
    type: "sync",
    room,
    from: "guided-room",
    uid: hostSessionId,
    grV: RELAY_VERSION,
    grKind: "hand-ack",
    grTarget: targetSessionId,
  });

/* Was that an acknowledgement aimed at us? Same strictness as parseHand,
   and note it does NOT check the sender: only a host has an ack button,
   and on an unauthenticated relay checking who sent it would buy nothing
   anyone couldn't forge anyway. The worst a forged ack does is lower a
   hand the buyer can raise again. */
export const parseHandAck = (data: unknown, room: string, selfId: string): boolean => {
  let msg: any;
  try {
    msg = typeof data === "string" ? JSON.parse(data) : data;
  } catch {
    return false;
  }
  if (!msg || typeof msg !== "object") return false;
  if (msg.type !== "sync") return false;
  if (String(msg.room || "") !== String(room)) return false;
  if (Number(msg.grV) !== RELAY_VERSION) return false;
  if (msg.grKind !== "hand-ack") return false;
  if (!selfId) return false;
  return String(msg.grTarget || "") === String(selfId);
};

/* ── Host-side hand state ──────────────────────────────────────────────
   A map of session_id → when they raised it. The timestamp is what makes
   the queue fair: whoever has been waiting longest is answered first,
   which is the whole reason a host wants this rather than a badge. */

export type Hands = Record<string, number>;

export const applyHand = (hands: Hands, sig: HandSignal, nowMs: number): Hands => {
  const has = Object.prototype.hasOwnProperty.call(hands, sig.sessionId);
  if (!sig.raised) {
    if (!has) return hands;                        // lowering a hand nobody had
    const next = { ...hands };
    delete next[sig.sessionId];
    return next;
  }
  /* Already up: keep the ORIGINAL timestamp. Re-announcements arrive
     every few seconds and refreshing the clock on each one would send the
     longest waiter to the back of their own queue. */
  if (has) return hands;
  return { ...hands, [sig.sessionId]: nowMs };
};

/* Someone who left the room can't still be asking a question. The host
   polls its roster anyway, so this runs against that list rather than
   needing a "left" frame the relay would drop.

   Returns the same object when nothing changed — a fresh one every poll
   would re-render the people list every five seconds forever. */
export const pruneHands = (hands: Hands, presentSessionIds: string[]): Hands => {
  const present = new Set(presentSessionIds.map((s) => String(s)));
  const keys = Object.keys(hands);
  const stale = keys.filter((k) => !present.has(k));
  if (stale.length === 0) return hands;
  const next = { ...hands };
  stale.forEach((k) => { delete next[k]; });
  return next;
};

/* Longest wait first. */
export const handQueue = (hands: Hands): string[] =>
  Object.keys(hands).sort((a, b) => hands[a] - hands[b]);

/* How long a hand has been up, for the host's row: "2m" reads as a
   reproach in a way "since 14:32" doesn't, which is the point — it should
   be mildly uncomfortable to leave someone waiting.

   Under a minute stays "just now" rather than counting seconds: a host
   watching a walkthrough doesn't need per-second precision, and a number
   that changes every second pulls the eye away from the tour. */
export const handWaitLabel = (raisedAtMs: number, nowMs: number): string => {
  const secs = Math.floor((nowMs - raisedAtMs) / 1000);
  if (!Number.isFinite(secs) || secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
};
